import { Box, Card, Stack, Typography, CircularProgress, Button, Alert, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, TextField, RadioGroup, FormControlLabel, Radio, Grid } from "@mui/material";
import { AccountBalance, Payments, ArrowForward } from "@mui/icons-material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import pxToRem from "../../../../Themes/Functions/pxToRem";
import { noScrollBarStyle } from "../../../../Configurations/AppStyle";
import { useEffect, useState } from "react";
import AppService from "../../../../Configurations/Appservice";
import { useDispatch } from "react-redux";
import DateRangeFilter from "../MyWallet/components/DateRangeFilter";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import moment from "moment";
import { Collapse, IconButton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

// Channel Partner Transactions Tab Component - Same UI as Service Partner Payments
const ChannelPartnerTransactionsTab = ({ channelPartners, transactionFilterPartnerId, setTransactionFilterPartnerId, dateFilter, setDateFilter }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch transactions on mount and when filter changes
  useEffect(() => {
    fetchTransactions();
  }, [transactionFilterPartnerId]);

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (transactionFilterPartnerId) {
        response = await AppService.get(`v1/channel-partner-payments/by-partner/${transactionFilterPartnerId}`);
      } else {
        response = await AppService.get("v1/channel-partner-payments/all");
      }
      setTransactions(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching channel partner transactions:", err);
      setError("Failed to load transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter transactions by date range
  const filteredTransactions = transactions.filter(txn => {
    if (!dateFilter.startDate && !dateFilter.endDate) return true;

    const txnDate = moment(txn.createdAt || txn.initiatedAt);
    const start = dateFilter.startDate ? moment(dateFilter.startDate) : null;
    const end = dateFilter.endDate ? moment(dateFilter.endDate).endOf('day') : null;

    if (start && end) {
      return txnDate.isBetween(start, end, null, '[]');
    } else if (start) {
      return txnDate.isSameOrAfter(start);
    } else if (end) {
      return txnDate.isSameOrBefore(end);
    }
    return true;
  });

  // Transaction Item Component - Same structure as Service Partner
  const TransactionItem = ({ item }) => {
    const [expanded, setExpanded] = useState(false);
    const hasTickets = item.tickets && item.tickets.length > 0;
    const ticketCount = item.ticketCount || item.tickets?.length || 0;

    const getStatusColor = (status) => {
      switch (status) {
        case 'COMPLETED':
        case 'CONFIRMED':
        case 'PAID':
          return 'success.main';
        case 'APPROVED':
        case 'PROCESSING':
        case 'TRANSFER_INITIATED':
          return 'info.main';
        case 'PENDING_APPROVAL':
        case 'PENDING':
          return 'warning.main';
        case 'REJECTED':
        case 'FAILED':
        case 'UNPAID':
          return 'error.main';
        default:
          return 'text.secondary';
      }
    };

    const formatStatus = (status) => {
      if (!status) return 'UNKNOWN';
      return status.replace(/_/g, ' ');
    };

    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          sx={{
            py: 1.5,
            px: 2,
            cursor: hasTickets ? 'pointer' : 'default',
            '&:hover': hasTickets ? { bgcolor: 'action.hover' } : {}
          }}
          onClick={() => hasTickets && setExpanded(!expanded)}
        >
          {/* Transaction Number */}
          <Box sx={{ width: '18%' }}>
            <Typography fontSize={12} fontWeight={500} color="text.secondary">
              {item.referenceNumber || "—"}
            </Typography>
          </Box>
          {/* Date */}
          <Box sx={{ width: '10%' }}>
            <Typography fontSize={13} fontWeight={500}>
              {item.createdAt ? moment(item.createdAt).format("YYYY-MM-DD") :
               item.initiatedAt ? moment(item.initiatedAt).format("YYYY-MM-DD") : "N/A"}
            </Typography>
          </Box>
          {/* Ticket Count */}
          <Box sx={{ width: '8%' }}>
            <Typography
              fontSize={13}
              color="primary.main"
              sx={{ cursor: hasTickets ? 'pointer' : 'default', '&:hover': hasTickets ? { textDecoration: 'underline' } : {} }}
            >
              {ticketCount} ticket{ticketCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
          {/* Partner Name */}
          <Box sx={{ width: '14%' }}>
            <Typography fontSize={13} color="text.secondary" noWrap>
              {item.channelPartnerName || "—"}
            </Typography>
          </Box>
          {/* Source Bank (From) */}
          <Box sx={{ width: '12%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {item.sourceBankAccountName || "—"}
            </Typography>
          </Box>
          {/* Destination Bank (To Partner) */}
          <Box sx={{ width: '12%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {item.partnerBankAccountName || "—"}
            </Typography>
          </Box>
          {/* Amount */}
          <Box sx={{ width: '13%', textAlign: 'right' }}>
            <Typography fontSize={14} fontWeight={600} color="primary.main">
              {item.amount ? Number(item.amount).toFixed(2) : "0.00"} AED
            </Typography>
          </Box>
          {/* Status */}
          <Box sx={{ width: '13%', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Typography fontSize={12} fontWeight={500} color={getStatusColor(item.status)}>
              {formatStatus(item.status)}
            </Typography>
            {hasTickets && (
              <IconButton size="small" sx={{ ml: 0.5 }}>
                {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Expanded Ticket Details */}
        <Collapse in={expanded}>
          {hasTickets && (
            <Box sx={{ bgcolor: 'grey.50', px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography fontSize={12} fontWeight={600} sx={{ mb: 1 }}>Tickets in this transaction:</Typography>
              {item.tickets.map((ticket, idx) => (
                <Box
                  key={ticket.ticketId || idx}
                  display="flex"
                  alignItems="center"
                  sx={{ py: 0.5 }}
                >
                  <Typography
                    fontSize={12}
                    color="primary.main"
                    sx={{ minWidth: 100, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      ticket.ticketId && window.open(`/ticket/${ticket.ticketId}`, '_blank');
                    }}
                  >
                    #{ticket.ticketNumber}
                  </Typography>
                  <Typography fontSize={12} color="text.secondary" sx={{ minWidth: 180, flex: 1 }}>
                    {ticket.serviceName || "—"}
                  </Typography>
                  <Typography fontSize={12} color="text.secondary" sx={{ minWidth: 150 }}>
                    {ticket.customerOrganisationName || "—"}
                  </Typography>
                  <Typography fontSize={12} color="text.secondary" sx={{ minWidth: 100 }}>
                    {ticket.commissionPercentage}% of {ticket.commissionType}
                  </Typography>
                  <Typography fontSize={12} fontWeight={500} sx={{ minWidth: 80, textAlign: 'right' }}>
                    {ticket.commissionAmount ? Number(ticket.commissionAmount).toFixed(2) : "0.00"} AED
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Channel Partner Transactions</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters Section */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        {/* Partner Filter */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="partner-filter-label">Filter by Partner</InputLabel>
          <Select
            labelId="partner-filter-label"
            value={transactionFilterPartnerId}
            onChange={(e) => setTransactionFilterPartnerId(e.target.value)}
            label="Filter by Partner"
          >
            <MenuItem value="">All Partners</MenuItem>
            {channelPartners.map((partner) => (
              <MenuItem key={partner.id} value={partner.id}>
                {partner.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range Filter */}
        <DateRangeFilter onFilterChange={setDateFilter} />
      </Box>

      {/* Table Header */}
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        sx={{ py: 1, px: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}
      >
        <Box sx={{ width: '18%' }}>
          <Typography fontSize={12} fontWeight={600}>Txn #</Typography>
        </Box>
        <Box sx={{ width: '10%' }}>
          <Typography fontSize={12} fontWeight={600}>Date</Typography>
        </Box>
        <Box sx={{ width: '8%' }}>
          <Typography fontSize={12} fontWeight={600}>Tickets</Typography>
        </Box>
        <Box sx={{ width: '14%' }}>
          <Typography fontSize={12} fontWeight={600}>Partner</Typography>
        </Box>
        <Box sx={{ width: '12%' }}>
          <Typography fontSize={12} fontWeight={600}>From Bank</Typography>
        </Box>
        <Box sx={{ width: '12%' }}>
          <Typography fontSize={12} fontWeight={600}>To Bank</Typography>
        </Box>
        <Box sx={{ width: '13%', textAlign: 'right' }}>
          <Typography fontSize={12} fontWeight={600}>Amount</Typography>
        </Box>
        <Box sx={{ width: '13%', textAlign: 'right' }}>
          <Typography fontSize={12} fontWeight={600}>Status</Typography>
        </Box>
      </Box>

      {/* Transaction List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', ...noScrollBarStyle }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : transactions.length > 0 ? (
          <Stack sx={{ overflow: "auto", flexGrow: 1 }}>
            {filteredTransactions.length > 0 ? (
              filteredTransactions.map((txn) => (
                <TransactionItem key={txn.id} item={txn} />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                No transactions found for the selected date range
              </Typography>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No transactions found
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ChannelPartnerPaymentPage = () => {
  const [unpaidTickets, setUnpaidTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const [selectedChannelPartnerId, setSelectedChannelPartnerId] = useState("");
  const [channelPartners, setChannelPartners] = useState([]);
  const [channelPartnersLoading, setChannelPartnersLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [transactionFilterPartnerId, setTransactionFilterPartnerId] = useState('');
  const dispatch = useDispatch();

  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [offlinePaymentType, setOfflinePaymentType] = useState('BANK_TRANSFER');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [partnerBankAccounts, setPartnerBankAccounts] = useState([]);
  const [selectedSourceBank, setSelectedSourceBank] = useState('');
  const [selectedPartnerBank, setSelectedPartnerBank] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null); // 'processing', 'success', 'error'
  const [processingMessage, setProcessingMessage] = useState('');

  // Finance overview data for tiles
  const [financeData, setFinanceData] = useState({
    totalBankBalance: 0,
    activeBankAccounts: 0,
    pendingChannelPartnerCommissions: 0,
    pendingChannelPartnerCount: 0
  });
  const [financeLoading, setFinanceLoading] = useState(false);

  // Update page title
  useEffect(() => {
    dispatch(updateScreenTitle("Channel Partner Commissions"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Fetch finance overview data for tiles.
  // Only the bank-wallet and channel-partner sub-sections are needed; calling
  // them in parallel avoids the slow monolithic /overview aggregation.
  useEffect(() => {
    const fetchFinanceData = async () => {
      setFinanceLoading(true);
      try {
        const [bank, channel] = await Promise.all([
          AppService.get("v1/admin/finance/overview/bank-wallet").catch(() => null),
          AppService.get("v1/admin/finance/overview/channel-partner").catch(() => null)
        ]);
        setFinanceData({
          totalBankBalance: bank?.totalBankBalance || 0,
          activeBankAccounts: bank?.activeBankAccounts || 0,
          pendingChannelPartnerCommissions: channel?.pendingApprovalChannelPartnerAmount || 0,
          pendingChannelPartnerCount: channel?.pendingApprovalChannelPartnerCount || 0
        });
      } catch (error) {
        console.error("Error fetching finance overview:", error);
      } finally {
        setFinanceLoading(false);
      }
    };
    fetchFinanceData();
  }, []);

  // Fetch channel partner organizations
  useEffect(() => {
    const fetchChannelPartners = async () => {
      setChannelPartnersLoading(true);
      try {
        const response = await AppService.get("v1/organisation/keyValues?type=CHANNEL_PARTNER");
        const list = Array.isArray(response) ? response : response?.data || [];
        setChannelPartners(list);
      } catch (err) {
        console.error("Error fetching channel partners:", err);
        setChannelPartners([]);
      } finally {
        setChannelPartnersLoading(false);
      }
    };
    fetchChannelPartners();
  }, []);

  // Fetch unpaid tickets when channel partner is selected
  useEffect(() => {
    if (selectedChannelPartnerId) {
      fetchUnpaidTicketsForPartner(selectedChannelPartnerId);
    } else {
      setUnpaidTickets([]);
    }
  }, [selectedChannelPartnerId]);

  const fetchUnpaidTicketsForPartner = async (channelPartnerId) => {
    setTicketsLoading(true);
    setError(null);
    try {
      const response = await AppService.get(`v1/channel-partner-payments/${channelPartnerId}/unpaid-tickets`);
      const data = response?.data || response;
      setUnpaidTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching unpaid tickets:", err);
      setError("Failed to fetch unpaid tickets");
      setUnpaidTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  // Handle ticket selection - fixed to work properly with checkboxes
  const handleSelectTicket = (event, ticketId) => {
    event.stopPropagation();
    setSelected(prev => {
      if (prev.includes(ticketId)) {
        return prev.filter(id => id !== ticketId);
      } else {
        return [...prev, ticketId];
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(unpaidTickets.map(t => t.ticketId));
    } else {
      setSelected([]);
    }
  };

  // Handle row click for selection
  const handleRowClick = (event, ticketId) => {
    // Don't trigger if clicking on checkbox directly
    if (event.target.type === 'checkbox') return;
    handleSelectTicket(event, ticketId);
  };

  // Calculate total selected commission
  const totalSelectedCommission = unpaidTickets
    .filter(t => selected.includes(t.ticketId))
    .reduce((sum, t) => sum + (t.commissionAmount || 0), 0);

  // Handle balance update after payment
  const handleBalanceUpdate = () => {
    // Refresh finance data via the two relevant sub-sections in parallel.
    Promise.all([
      AppService.get("v1/admin/finance/overview/bank-wallet").catch(() => null),
      AppService.get("v1/admin/finance/overview/channel-partner").catch(() => null)
    ])
      .then(([bank, channel]) => {
        setFinanceData({
          totalBankBalance: bank?.totalBankBalance || 0,
          activeBankAccounts: bank?.activeBankAccounts || 0,
          pendingChannelPartnerCommissions: channel?.pendingApprovalChannelPartnerAmount || 0,
          pendingChannelPartnerCount: channel?.pendingApprovalChannelPartnerCount || 0
        });
      })
      .catch(err => console.error("Error refreshing finance data:", err));
    // Refresh tickets after payment
    if (selectedChannelPartnerId) {
      fetchUnpaidTicketsForPartner(selectedChannelPartnerId);
    }
    setSelected([]);
  };

  // Metric Tile Component - same UI as Service Partner Payments page
  const MetricTile = ({ title, amount, subtitle, icon: Icon, color, onClick }) => (
    <Card
      sx={{
        background: color,
        borderRadius: 2,
        p: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        } : {},
        position: 'relative',
        overflow: 'hidden',
        minHeight: 130
      }}
      onClick={onClick}
    >
      {/* Icon in top right */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          opacity: 0.15,
          color: 'white'
        }}
      >
        <Icon sx={{ fontSize: 42 }} />
      </Box>

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: 'rgba(255, 255, 255, 0.95)',
            fontWeight: 500,
            mb: 1.5,
            fontSize: '0.75rem'
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="h4"
          sx={{
            color: '#fff',
            fontWeight: 700,
            mb: 0.75,
            fontSize: '1.4rem',
            lineHeight: 1.2
          }}
        >
          {amount}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: '0.65rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          {subtitle}
          {onClick && <ArrowForward sx={{ fontSize: 14 }} />}
        </Typography>
      </Box>
    </Card>
  );

  // Helper function to format currency
  const formatCurrency = (amount, currency = 'AED') => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-AE', { style: 'currency', currency }).format(amount);
  };

  // Open payment dialog
  const handleOpenPaymentDialog = async () => {
    if (selected.length === 0) return;

    try {
      // Fetch source bank accounts — any ACTIVE Thaklis bank can fund a
      // channel-partner commission payment (including gateway settlement
      // accounts), so the type filter is intentionally absent here.
      const bankResponse = await AppService.get("/v1/admin/bank-accounts");
      const allAccounts = Array.isArray(bankResponse) ? bankResponse : bankResponse?.data || [];
      const activeAccounts = allAccounts.filter(acc => acc.status === 'ACTIVE');
      setBankAccounts(activeAccounts);

      // Fetch partner bank accounts
      if (selectedChannelPartnerId) {
        try {
          const partnerBanks = await AppService.get(`/v1/partner-bank-accounts/partner/${selectedChannelPartnerId}`);
          setPartnerBankAccounts(Array.isArray(partnerBanks) ? partnerBanks : []);
        } catch (err) {
          console.warn('Failed to load partner bank accounts:', err);
          setPartnerBankAccounts([]);
        }
      }

      // Reset dialog state
      setPaymentMethod(null);
      setOfflinePaymentType('BANK_TRANSFER');
      setSelectedSourceBank('');
      setSelectedPartnerBank('');
      setShowPaymentDialog(true);
    } catch (err) {
      console.error('Error loading bank accounts:', err);
      setError('Failed to load bank accounts');
    }
  };

  // Process payment
  const handleProcessPayment = async () => {
    if (!paymentMethod) {
      setError('Please select a payment method');
      return;
    }
    if (!selectedSourceBank) {
      setError('Please select a source bank account');
      return;
    }
    if (!selectedPartnerBank) {
      setError('Please select a partner bank account');
      return;
    }

    setProcessing(true);
    setError(null);
    setShowPaymentDialog(false);
    setProcessingStatus('processing');
    setProcessingMessage('Processing your payment...');

    try {
      const requestBody = {
        channelPartnerId: selectedChannelPartnerId,
        amount: totalSelectedCommission,
        paymentMethod: paymentMethod === 'OFFLINE' ? offlinePaymentType : paymentMethod,
        partnerBankAccountId: selectedPartnerBank,
        sourceBankAccountId: selectedSourceBank,
        description: `Channel partner commission payment for ${selected.length} ticket(s)`,
        ticketIds: selected
      };

      await AppService.post('v1/channel-partner-payments/initiate', requestBody);

      setProcessingStatus('success');
      setProcessingMessage('Payment processed successfully!');

      // Reset states after success
      setTimeout(() => {
        setProcessingStatus(null);
        setSelected([]);
        setSelectedSourceBank('');
        setSelectedPartnerBank('');
        setPaymentMethod(null);
        // Refresh tickets and finance data
        fetchUnpaidTicketsForPartner(selectedChannelPartnerId);
        handleBalanceUpdate();
      }, 3000);
    } catch (err) {
      console.error('Error processing payment:', err);
      setProcessingStatus('error');
      setProcessingMessage(err?.response?.data?.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${pxToRem(55)})`,
        width: "100%",
      }}
    >
      {/* Metric Tiles - Same UI as Service Partner Payments */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          {/* Total Bank Balance */}
          <Grid item xs={12} md={6}>
            <MetricTile
              title="Total Bank Balance"
              amount={`AED ${Number(financeData.totalBankBalance || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={`${financeData.activeBankAccounts} active account${financeData.activeBankAccounts !== 1 ? 's' : ''}`}
              icon={AccountBalance}
              color="#1a7f8f"
            />
          </Grid>

          {/* Pending Channel Partner Commissions */}
          <Grid item xs={12} md={6}>
            <MetricTile
              title="Pending Partner Commission"
              amount={`AED ${Number(financeData.pendingChannelPartnerCommissions || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={`${financeData.pendingChannelPartnerCount} partner${financeData.pendingChannelPartnerCount !== 1 ? 's' : ''} • Awaiting payment`}
              icon={Payments}
              color="#1f6b7a"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                sx={{ px: 2 }}
              >
                <Tab label="SELECT TICKET & PAYMENT" />
                <Tab label="CHANNEL PARTNER TRANSACTIONS" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {/* Tab 0: Select Channel Partner & Tickets for Payment */}
              {currentTab === 0 && (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
                  {/* Channel Partner Selection Dropdown */}
                  <Box sx={{ mb: 2 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="channel-partner-select">Select Channel Partner</InputLabel>
                      <Select
                        labelId="channel-partner-select"
                        value={selectedChannelPartnerId}
                        onChange={(e) => {
                          setSelectedChannelPartnerId(e.target.value);
                          setSelected([]);
                        }}
                        label="Select Channel Partner"
                        disabled={channelPartnersLoading}
                      >
                        {channelPartners.map((partner) => (
                          <MenuItem key={partner.id} value={partner.id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}

                  {/* Ticket Table */}
                  {selectedChannelPartnerId && (
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                      {ticketsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                          <CircularProgress />
                        </Box>
                      ) : unpaidTickets.length > 0 ? (
                        <>
                          {/* Summary Bar */}
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            mb: 2, 
                            p: 1.5, 
                            bgcolor: 'primary.main', 
                            borderRadius: 1,
                            color: 'white'
                          }}>
                            <Typography variant="body2">
                              Selected: {selected.length} of {unpaidTickets.length} tickets
                            </Typography>
                            <Typography variant="body1" fontWeight={700}>
                              Total Commission: AED {totalSelectedCommission.toFixed(2)}
                            </Typography>
                          </Box>

                          {/* Tickets Table */}
                          <Table size="small" sx={{ bgcolor: 'background.paper' }}>
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#16697A' }}>
                                <TableCell padding="checkbox" sx={{ color: 'white' }}>
                                  <Checkbox
                                    checked={unpaidTickets.length > 0 && selected.length === unpaidTickets.length}
                                    indeterminate={selected.length > 0 && selected.length < unpaidTickets.length}
                                    onChange={handleSelectAll}
                                    sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                                  />
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Ticket #</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Service Name</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Referred Organization</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Ticket Amount</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Distribution</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Commission</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {unpaidTickets.map((ticket) => (
                                <TableRow 
                                  key={ticket.ticketId} 
                                  hover 
                                  selected={selected.includes(ticket.ticketId)}
                                  onClick={(e) => handleRowClick(e, ticket.ticketId)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selected.includes(ticket.ticketId)}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => handleSelectTicket(e, ticket.ticketId)}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600} color="primary.main">
                                      {ticket.ticketNumber}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{ticket.serviceName || '-'}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{ticket.customerOrganisationName || '-'}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">AED {(ticket.ticketAmount || 0).toFixed(2)}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {ticket.commissionPercentage}% of {ticket.commissionType || 'TICKET'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontWeight={600} color="warning.main">
                                      AED {(ticket.commissionAmount || 0).toFixed(2)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>

                          {/* Process Payment Button */}
                          {selected.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
                              {successMessage && (
                                <Alert severity="success" sx={{ py: 0 }}>
                                  {successMessage}
                                </Alert>
                              )}
                              <Button
                                variant="contained"
                                color="primary"
                                disabled={selected.length === 0 || processing}
                                onClick={handleOpenPaymentDialog}
                                sx={{ px: 4 }}
                              >
                                {processing ? 'Processing...' : `Process Payment (AED ${totalSelectedCommission.toFixed(2)})`}
                              </Button>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                          <Typography variant="body2">No unpaid tickets found for this channel partner</Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {channelPartnersLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  {!channelPartnersLoading && channelPartners.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body2">No channel partners available</Typography>
                    </Box>
                  )}

                  {!channelPartnersLoading && channelPartners.length > 0 && !selectedChannelPartnerId && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body2">Please select a channel partner to view tickets</Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 1: Channel Partner Transactions */}
              {currentTab === 1 && (
                <ChannelPartnerTransactionsTab
                  channelPartners={channelPartners}
                  transactionFilterPartnerId={transactionFilterPartnerId}
                  setTransactionFilterPartnerId={setTransactionFilterPartnerId}
                  dateFilter={dateFilter}
                  setDateFilter={setDateFilter}
                />
              )}
            </Box>
          </Card>
        </Box>
      </Box>
      {/* Payment Method Selection Dialog - Same UI as Service Partner */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#064d51' }}>
          Select Payment Method
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {/* Total Amount Display */}
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Total Commission to Pay
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#064d51' }}>
              {formatCurrency(totalSelectedCommission)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selected.length} ticket(s) selected
            </Typography>
          </Box>

          {/* Payment Method Selection */}
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
            Payment Method
          </Typography>
          <RadioGroup
            value={paymentMethod || ''}
            onChange={(e) => setPaymentMethod(e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel
              value="ONLINE"
              control={<Radio />}
              label={<Typography variant="body2">Online Payment</Typography>}
              sx={{
                mb: 1,
                p: 1.5,
                border: paymentMethod === 'ONLINE' ? '2px solid #064d51' : '1px solid #e0e0e0',
                borderRadius: 1,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(6, 77, 81, 0.05)' }
              }}
            />
            <FormControlLabel
              value="OFFLINE"
              control={<Radio />}
              label={<Typography variant="body2">Offline Payment</Typography>}
              sx={{
                p: 1.5,
                border: paymentMethod === 'OFFLINE' ? '2px solid #064d51' : '1px solid #e0e0e0',
                borderRadius: 1,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(6, 77, 81, 0.05)' }
              }}
            />
          </RadioGroup>

          {/* Offline Payment Type Selection - only show when OFFLINE is selected */}
          {paymentMethod === 'OFFLINE' && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Payment Type
              </Typography>
              <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 3 }}>
                <InputLabel id="payment-type-label">Select Payment Type</InputLabel>
                <Select
                  labelId="payment-type-label"
                  value={offlinePaymentType}
                  onChange={(e) => setOfflinePaymentType(e.target.value)}
                  label="Select Payment Type"
                >
                  <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                  <MenuItem value="CHEQUE">Cheque</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {/* Source Bank Account Selection */}
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
            Source Bank Account (Thaklis)
          </Typography>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="bank-account-label">Select Bank Account</InputLabel>
            <Select
              labelId="bank-account-label"
              value={selectedSourceBank || ''}
              onChange={(e) => setSelectedSourceBank(e.target.value)}
              label="Select Bank Account"
            >
              <MenuItem value="" disabled>
                Choose a bank account
              </MenuItem>
              {bankAccounts.length > 0 ? (
                bankAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {account.bankName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {account.accountNumber && `${account.accountNumber}`} {(account.accountName || account.name) && `(${account.accountName || account.name})`}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: '#064d51', fontWeight: 500, mt: 0.5 }}>
                        Balance: {formatCurrency(account.balance || 0)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No bank accounts available
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Destination Bank Account Selection (Partner's Bank) */}
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, mt: 3 }}>
            Destination Bank Account (Channel Partner)
          </Typography>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="partner-bank-label">Select Partner Bank</InputLabel>
            <Select
              labelId="partner-bank-label"
              value={selectedPartnerBank || ''}
              onChange={(e) => setSelectedPartnerBank(e.target.value)}
              label="Select Partner Bank"
            >
              <MenuItem value="" disabled>
                Choose partner bank account
              </MenuItem>
              {partnerBankAccounts.length > 0 ? (
                partnerBankAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {account.bankName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {account.accountNumber} {account.accountHolderName && `(${account.accountHolderName})`}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No partner bank accounts configured
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Info Box */}
          <Box sx={{ mt: 2.5, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, borderLeft: '4px solid #1976d2' }}>
            <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 500 }}>
              Payment will be transferred from Thaklis bank to channel partner's bank account.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowPaymentDialog(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcessPayment}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#064d51',
              color: '#ffffff',
              '&:hover': { bgcolor: '#043a3e' },
              '&:disabled': { bgcolor: '#d0d0d0', color: '#909090' }
            }}
            disabled={!paymentMethod || !selectedSourceBank || !selectedPartnerBank}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Processing Status Dialog */}
      <Dialog
        open={processingStatus !== null}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)' }
        }}
      >
        <DialogContent sx={{ py: 4, px: 3, textAlign: 'center', minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          {processingStatus === 'processing' && (
            <>
              <Box sx={{ mb: 2 }}>
                <CircularProgress size={60} sx={{ color: '#064d51' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#064d51' }}>
                Processing Payment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {processingMessage}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 2 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#064d51', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#064d51', animation: 'pulse 1.5s ease-in-out 0.2s infinite' }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#064d51', animation: 'pulse 1.5s ease-in-out 0.4s infinite' }} />
              </Box>
            </>
          )}

          {processingStatus === 'success' && (
            <>
              <Box sx={{ mb: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 70, color: '#4caf50' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#064d51' }}>
                Payment Successful
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {processingMessage}
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50', mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                  Amount Paid: {formatCurrency(totalSelectedCommission)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                The payment has been processed and will be reflected shortly.
              </Typography>
            </>
          )}

          {processingStatus === 'error' && (
            <>
              <Box sx={{ mb: 2 }}>
                <ErrorIcon sx={{ fontSize: 70, color: '#d32f2f' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#064d51' }}>
                Payment Failed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {processingMessage}
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #d32f2f', mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 500 }}>
                  Please try again or contact support for assistance.
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#064d51',
                  '&:hover': { bgcolor: '#043a3e' }
                }}
                onClick={() => {
                  setProcessingStatus(null);
                  setProcessingMessage('');
                }}
              >
                Try Again
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Box>
  );
};

export default ChannelPartnerPaymentPage;
